// ─── Equipment Maintenance & Calibration Planner — domain dictionary ──────────
//
// Single source of truth for the planner's terminology (event types, cycles,
// equipment groups). Centralized here so labels can later be swapped for i18n
// keys without touching the components that render them.

// ─── Event types ────────────────────────────────────────────────────────────

// Event types are dynamic and tenant-scoped: the catalog is fetched from the API
// (`useEventTypes`) and each type carries a single hex `color`. `EventType` is
// therefore just the type's stable `code` string (e.g. "preventive_maintenance").
export type EventType = string

/** Resolved event-type metadata — code plus its display label and hex color. */
export type EventTypeMeta = {
  code: string
  label: string
  color: string
}

/** Look up an event-type's `{ label, color }` by code (used by chips/calendar). */
export type ResolveEventMeta = (code: string) => { label: string; color: string }

/** Compact labels for the standard event-type codes, used in dense chips. */
const SHORT_LABELS: Record<string, string> = {
  preventive_maintenance: 'PM',
  corrective_maintenance: 'CM',
  corrective_action: 'CA',
  cleaning: 'Cleaning',
  calibration: 'Calibration',
  inspection: 'Inspection',
  verification: 'Verification',
  performance_qualification: 'PQ',
  process: 'Process',
}

/**
 * A compact label for a grid chip. Standard codes get a curated short label;
 * otherwise derive one — a parenthetical abbreviation if the label has one
 * (e.g. "Preventive Maintenance (PM)" → "PM"), else an uppercase initialism of
 * the alphanumeric words, else a short truncation.
 */
export function shortLabelFor(code: string, label: string): string {
  const known = SHORT_LABELS[code]
  if (known) return known
  const trimmed = label.trim()
  // An explicit abbreviation in parentheses wins, e.g. "… (PM)" → "PM".
  const paren = trimmed.match(/\(([A-Za-z0-9/&+-]+)\)/)
  if (paren) return paren[1].toUpperCase()
  // Initialism from alphanumeric words only (so punctuation never leaks in).
  const words = trimmed.replace(/[^A-Za-z0-9\s]+/g, ' ').split(/\s+/).filter(Boolean)
  if (words.length >= 2) {
    return words
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 4)
  }
  return words[0] ? words[0].slice(0, 10) : trimmed.slice(0, 10)
}

/** Solid chip styling from a hex color — filled background, white text. */
export function eventChipStyle(color: string): { backgroundColor: string; color: string } {
  return { backgroundColor: color, color: '#fff' }
}

/** Soft/selected styling from a hex color — tinted background + colored text/border. */
export function eventSoftStyle(color: string): {
  backgroundColor: string
  color: string
  borderColor: string
} {
  return { backgroundColor: `${color}1a`, color, borderColor: `${color}55` }
}

/** Dot/swatch styling from a hex color. */
export function eventDotStyle(color: string): { backgroundColor: string } {
  return { backgroundColor: color }
}

// ─── Recurrence cycles ──────────────────────────────────────────────────────

export type Cycle =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'semiannually'
  | 'yearly'

export const CYCLE_LABELS: Record<Cycle, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannually: 'Semi-Annually',
  yearly: 'Yearly',
}

/** Column order for the recurrence-cycle block of the grid. */
export const CYCLE_ORDER: Cycle[] = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'semiannually',
  'yearly',
]

// ─── Equipment groups ───────────────────────────────────────────────────────

// Groups are dynamic and tenant-scoped: a group is identified by its unique
// name string, which is also its display label. The list of groups is fetched
// from the API (`useGroups`) rather than hard-coded here.
export type EquipmentGroup = string

/**
 * What a type of equipment is FOR — mirrors the backend `EquipmentPurpose` enum
 * (`prisma/schema.prisma`). Part of the domain dictionary duplicated by design
 * across the two repos; changing one side without the other silently desyncs.
 *
 * A coarser axis than `EquipmentGroup`, and independent of it: group names a
 * manufacturing stage, and Cryopreservation covers both a controlled-rate
 * freezer (processing) and an LN2 dewar (storage).
 *
 * Unrelated to inventory's storage zones, which are where stock physically sits.
 */
export type EquipmentPurpose = 'PROCESSING' | 'STORAGE' | 'ROOM'

/** The purpose a type has when the backend sends none — matches the column default. */
export const DEFAULT_EQUIPMENT_PURPOSE: EquipmentPurpose = 'PROCESSING'

/**
 * The purposes the Equipment page lists and offers — every one but Room. Rooms
 * are set up and listed on the Cleaning page only, so neither its purpose
 * filter nor Add/Edit Equipment Type offers Room (z8nrz7ewt2).
 */
export const EQUIPMENT_PURPOSES: EquipmentPurpose[] = ['PROCESSING', 'STORAGE']

/**
 * A room is equipment with purpose ROOM (z8nrz7ewt2). The Cleaning page lists
 * these and nothing else; the Equipment page never lists them.
 */
export const ROOM_PURPOSES: EquipmentPurpose[] = ['ROOM']

/**
 * Storage units only: Inventory's Tasks tab (z8nrz7fjzc). They stay on the
 * Equipment page as well, where they are set up.
 */
export const STORAGE_PURPOSES: EquipmentPurpose[] = ['STORAGE']

/**
 * The materials Inventory's Tasks lists tasks on (z8nrz7fjzc): its Consumables,
 * Reagents and Kits tabs. Matched without regard to case.
 */
export const INVENTORY_TASK_MATERIAL_TYPES: readonly string[] = ['Consumable', 'Reagent', 'Kit']

/** Display names. The wire carries the enum; screens never show it raw. */
export const EQUIPMENT_PURPOSE_LABELS: Record<EquipmentPurpose, string> = {
  PROCESSING: 'Processing',
  STORAGE: 'Storage',
  ROOM: 'Room',
}

/**
 * Seed group display names — the names created by the backend seed. Used only
 * as a cosmetic fallback (e.g. mock mode) when no groups have been fetched.
 */
export const SEED_GROUP_NAMES: string[] = [
  'Cell Collection (Apheresis)',
  'Cell Processing',
  'Cell Expansion & Culture',
  'Cryopreservation',
  'Genetic Modification',
]

// ─── Equipment units + schedules ────────────────────────────────────────────

export type EquipmentLocation = {
  site: string
  floor?: string
  bay?: string
  /** Pre-formatted display string, e.g. "Building-5 / Floor-1 / Bay-4" or "Suite-A". */
  display: string
}

/** Weekly / Bi-Weekly tri-state day picker: a day is either required or preferred (★). */
export type WeeklyDayState = { day: number; state: 'required' | 'preferred' }

/** What happens in a given cycle column for one equipment row. */
export type CycleScheduleEntry = {
  eventTypes: EventType[]
  /** Small muted subtext under the chip(s), e.g. "2×/wk + M". */
  note?: string
  /** Weekly / Bi-Weekly day-picker selection — kept structurally so edits round-trip. */
  weeklyDays?: WeeklyDayState[]
  /** Monthly+ "preferred timing within period" selection. */
  preferredTiming?: string
  /** Optional time of day (HH:mm 24h). Alone = a single time; with endTime = a window start. */
  startTime?: string
  /** Optional window end time (HH:mm 24h). */
  endTime?: string
  /**
   * The procedure each event type on this cycle runs, by event type code
   * (z8nrz7f9u6): monthly PM one form, monthly calibration another. A Cleaning
   * template on a room type, an Equipment template otherwise.
   */
  procedures?: CycleProcedures
}

/** Event type code → the procedure it runs on a cycle. */
export type CycleProcedures = Partial<Record<EventType, { id: string; name: string }>>

export type EquipmentSchedule = Partial<Record<Cycle, CycleScheduleEntry>>

/** A single non-routine, date-anchored event (Calendar column). */
export type CalendarEventEntry = {
  /** Backend row id (absent for locally-added, not-yet-saved entries). */
  id?: string
  /** ISO date string, e.g. "2026-09-15". */
  date: string
  /** One of NON_ROUTINE_EVENTS. */
  event: string
  note?: string
}

/** Non-routine, date-anchored events shown in the Calendar column. */
export type CalendarInfo = {
  events: CalendarEventEntry[]
}

/** Predefined non-routine event catalog (Calendar-based events modal). */
export const NON_ROUTINE_EVENTS = [
  'Annual Calibration',
  'Preventive Maintenance (Vendor)',
  'Requalification (IQ/OQ/PQ)',
  'Safety Inspection',
  'HEPA Replacement',
  'Firmware Update',
  'Warranty Visit',
  'Decommissioning',
]

/** A single authored pre-start checklist item for an equipment type. */
export type PreStartChecklistItem = {
  id: string
  label: string
  required: boolean
  order: number
}

export type Equipment = {
  id: string
  /** Equipment ID as shown in the grid, e.g. "APH-A1". */
  equipmentId: string
  /** Equipment Type, e.g. "Apheresis machines". */
  equipmentType: string
  /** Backend EquipmentType row id (absent in mock mode). */
  equipmentTypeId?: string
  group: EquipmentGroup
  /** Inherited from the unit's type — every unit of a type shares its purpose. */
  purpose: EquipmentPurpose
  /**
   * Whether this unit's type is serviced on a cycle. Inherited, never set per
   * unit: schedules belong to the type, so one unit of a passive type cannot be
   * maintained while its siblings are not.
   */
  maintained: boolean
  location: EquipmentLocation
  /** One-line description shown in the Information tooltip. */
  description: string
  /** Asset registry details shown in the Information modal. */
  model: string
  serial: string
  vendor: string
  /** Installation year, e.g. 2024. */
  installed: number
  schedule: EquipmentSchedule
  /** OUT_OF_SERVICE units are refused at booking. Absent in mock mode. */
  status?: 'OPERATIONAL' | 'OUT_OF_SERVICE'
  /** A room's floor in the site layout (z8nrz7f731). Null = not mapped yet; absent in mock mode. */
  floorId?: string | null
  /** The room this unit stands in (z8nrz7fw25). Null = in none yet; absent in mock mode. */
  roomId?: string | null
  /** Rooms: cleanroom grade ("B", "C/D", "CNC") and floor area in m² (Cleaning Visual). */
  grade?: string | null
  areaSqm?: number | null
  calendar?: CalendarInfo
  /** Ordered pre-start checklist authored for this equipment's type. */
  preStartChecklist?: PreStartChecklistItem[]
}

// ─── Schedule configuration (Configure Schedule modal) ──────────────────────

/** Cycles that use the tri-state weekday picker instead of the timing dropdown. */
export const DAY_PICKER_CYCLES: Cycle[] = ['weekly', 'biweekly']

/** "Preferred timing within period" options, keyed by cycle. */
export const PREFERRED_TIMING_OPTIONS: Partial<Record<Cycle, string[]>> = {
  monthly: ['Start of month', 'Mid-month', 'End of month'],
  quarterly: ['First month', 'Second month', 'Third month'],
  semiannually: ['First half', 'Second half'],
  yearly: ['Q1', 'Q2', 'Q3', 'Q4'],
}

/** Weekday labels for the tri-state picker (Sun–Sat). */
export const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
export const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * A row in the Equipment Type view — one per distinct equipment type. Schedules
 * are defined per type, so the schedule/calendar are shared by all its units.
 */
export type EquipmentTypeRow = {
  /** Backend EquipmentType row id (absent in mock mode). */
  id?: string
  equipmentType: string
  group: EquipmentGroup
  purpose: EquipmentPurpose
  /**
   * Whether this kind of machine is serviced on a cycle at all.
   *
   * A separate axis from `purpose`: a pharmacy fridge is storage AND
   * calibrated, ambient shelving is storage and never touched. False hides the
   * whole scheduling surface — the backend refuses schedules and calendar
   * events for the type, and skips it when generating maintenance.
   */
  maintained: boolean
  /** Room types (Cleaning Visual): hours a clean holds; null = not set. */
  cleanHoldTimeHours?: number | null
  units: number
  schedule: EquipmentSchedule
  calendar?: CalendarInfo
  /** Ordered pre-start checklist authored for this type. */
  preStartChecklist?: PreStartChecklistItem[]
  /** Technician a generated PM task for this type is assigned to, if any. */
  defaultAssigneeId?: string
  defaultAssigneeName?: string
}

/** Footer note — centralized so an i18n key can drop in later. */
export const SCHEDULE_SCOPE_NOTE =
  'schedules are defined per Equipment Type and apply to all units of that type.'
